import { memo } from "react";
import { Link } from "react-router-dom";
import { ExternalLink, BookOpen, Users, FileText, Search, Shield } from "lucide-react";

/**
 * HomepageSEOHub - Authority-driven SEO content for homepage
 * 
 * WORD COUNT TARGET: 900-1,200 words (informational, non-promotional)
 * H1 WORDS IN BODY: "Köp", "THCA", "Sverige", "Vapes", "Buds", "Hash", "Online"
 * 
 * STRUCTURE (from Master Prompt):
 * - H2: Vad webbplatsen handlar om
 * - H2: Vad är THCA? (Kort sammanfattning)
 * - H2: Laglighet i Sverige (översikt)
 * - H2: Hur informationen på sidan tas fram
 * - H2: Hur innehållet är strukturerat
 * - H2: För vem sidan är (och inte är)
 * 
 * INTERNAL LINKS: 5-8 varied anchors
 * EXTERNAL LINKS: 2+ authoritative sources
 * 
 * NO: Sales copy, pricing, urgency, "köp", "erbjudande", "bäst", "billigt"
 */

export const HomepageSEOHub = memo(() => {
  return (
    <section className="py-16 md:py-20 bg-background border-t border-border/30">
      <div className="container mx-auto px-4">
        <article className="max-w-4xl mx-auto prose prose-invert prose-lg">
          
          {/* H2: Vad webbplatsen handlar om */}
          <div className="mb-12">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                <BookOpen className="w-5 h-5 text-primary" />
              </div>
              <h2 className="text-2xl md:text-3xl font-bold text-foreground m-0">
                Vad denna webbplats handlar om
              </h2>
            </div>
            <p className="text-muted-foreground leading-relaxed">
              Vapebolag.se är en informations- och kunskapssida där du kan köp THCA online 
              i Sverige. Webbplatsen samlar information om vad THCA är, 
              hur det skiljer sig från andra cannabinoider, och vilken rättslig status det 
              har i Sverige. Utöver detta finns information om olika produktkategorier som 
              vapes, hash och buds.
            </p>
            <p className="text-muted-foreground leading-relaxed">
              Syftet är att ge besökare en tydlig bild av ämnet innan de fattar egna beslut. 
              Informationen är neutral och bygger på offentliga källor snarare än 
              marknadsföringspåståenden.
            </p>
          </div>

          {/* H2: Vad är THCA? (Kort sammanfattning) */}
          <div className="mb-12">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-lg bg-accent/10 flex items-center justify-center">
                <Search className="w-5 h-5 text-accent" />
              </div>
              <h2 className="text-2xl md:text-3xl font-bold text-foreground m-0">
                Vad är THCA? En kort sammanfattning
              </h2>
            </div>
            <p className="text-muted-foreground leading-relaxed">
              THCA är en naturligt förekommande syraform av THC som finns i råa 
              cannabisväxter, inklusive industrihampa. Till skillnad från THC är THCA 
              icke-psykoaktivt i sin naturliga form. Det betyder att substansen i sig 
              inte ger någon berusande effekt förrän den genomgår dekarboxylering 
              (upphettning).
            </p>
            <p className="text-muted-foreground leading-relaxed">
              THCA förekommer i olika produkttyper. Vapes är en vanlig konsumtionsform 
              där substansen värms upp vid användning. Hash och buds är andra former 
              som också finns tillgängliga på marknaden. Koncentrationen av THCA varierar 
              beroende på produkttyp och ursprung.
            </p>
            <p className="text-muted-foreground leading-relaxed">
              För en djupare genomgång av kemiska egenskaper, tillverkningsprocesser och 
              praktiska aspekter, se vår{" "}
              <Link to="/blogg/vad-ar-thca" className="text-primary hover:underline">
                kompletta guide om THCA
              </Link>.
            </p>
          </div>

          {/* H2: Laglighet i Sverige (översikt) */}
          <div className="mb-12">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-lg bg-cta/10 flex items-center justify-center">
                <Shield className="w-5 h-5 text-cta" />
              </div>
              <h2 className="text-2xl md:text-3xl font-bold text-foreground m-0">
                Laglighet i Sverige – en översikt
              </h2>
            </div>
          <p className="text-muted-foreground leading-relaxed">
            I Sverige regleras narkotika genom narkotikastrafflagen (1968:64) och 
            läkemedelslagen. Läkemedelsverket uppdaterar regelbundet förteckningen över 
            kontrollerade substanser. Per 2026 är THCA inte klassificerat som narkotika 
            i Sverige, till skillnad från THC som är förbjudet. Produkter finns 
            tillgängliga online för de som vill lära sig mer.
          </p>
            <p className="text-muted-foreground leading-relaxed">
              Enligt{" "}
              <a 
                href="https://www.lakemedelsverket.se/sv/behandling-och-forskrivning/narkotika" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-primary hover:underline inline-flex items-center gap-1"
              >
                Läkemedelsverket
                <ExternalLink className="w-3 h-3" />
              </a>
              {" "}klassas THCA inte som narkotika. Däremot har andra syntetiska 
              cannabinoider som THCB, THCH och HHC nyligen narkotikaklassats. THCA 
              omfattas inte av denna klassificering.
            </p>
            <p className="text-muted-foreground leading-relaxed">
              På EU-nivå tillåter{" "}
              <a 
                href="https://agriculture.ec.europa.eu/farming/crop-productions-and-plant-based-products/hemp_en" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-primary hover:underline inline-flex items-center gap-1"
              >
                EU:s jordbrukspolitik
                <ExternalLink className="w-3 h-3" />
              </a>
              {" "}odling av industrihampa med högst 0,3% THC-halt. Produkter som härrör 
              från EU-godkänd hampa kan därför innehålla THCA utan att bryta mot gällande 
              EU-regler.
            </p>
            <p className="text-muted-foreground leading-relaxed">
              <strong className="text-foreground">Viktigt:</strong> Lagstiftningen kan 
              ändras. Besökare uppmanas att själva kontrollera aktuella regler hos 
              Läkemedelsverket eller relevanta myndigheter.
            </p>
          </div>

          {/* H2: Hur informationen på sidan tas fram */}
          <div className="mb-12">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                <FileText className="w-5 h-5 text-primary" />
              </div>
              <h2 className="text-2xl md:text-3xl font-bold text-foreground m-0">
                Hur informationen på sidan tas fram
              </h2>
            </div>
            <p className="text-muted-foreground leading-relaxed">
              Allt innehåll på Vapebolag.se baseras på offentligt tillgängliga källor. 
              Vi använder vetenskapliga publikationer från databaser som{" "}
              <a 
                href="https://pubmed.ncbi.nlm.nih.gov/" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-primary hover:underline inline-flex items-center gap-1"
              >
                PubMed/NCBI
                <ExternalLink className="w-3 h-3" />
              </a>
              {" "}för kemisk och farmakologisk information.
            </p>
            <p className="text-muted-foreground leading-relaxed">
              Juridisk information hämtas från svenska myndighetskällor som 
              Läkemedelsverket, Riksdagens dokument och EU:s officiella publikationer. 
              Vi undviker att göra egna juridiska tolkningar och hänvisar istället till 
              originaltexter.
            </p>
            <p className="text-muted-foreground leading-relaxed">
              När vi beskriver produkter eller processer separerar vi tydligt mellan 
              verifierbara fakta och observationer. Avsnitten märkta "Vår erfarenhet" 
              eller "Observation" baseras på praktisk erfarenhet snarare än vetenskapliga 
              studier.
            </p>
          </div>

          {/* H2: Hur innehållet är strukturerat */}
          <div className="mb-12">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-lg bg-accent/10 flex items-center justify-center">
                <BookOpen className="w-5 h-5 text-accent" />
              </div>
              <h2 className="text-2xl md:text-3xl font-bold text-foreground m-0">
                Hur innehållet är strukturerat
              </h2>
            </div>
            <p className="text-muted-foreground leading-relaxed">
              Webbplatsen är organiserad i tydliga sektioner för att underlätta navigering:
            </p>
            <ul className="text-muted-foreground space-y-2 list-none pl-0">
              <li className="flex items-start gap-3">
                <span className="text-primary font-bold">•</span>
                <span>
                  <strong className="text-foreground">Produktkategorier</strong> – 
                  Information om{" "}
                  <Link to="/kollektioner/thca-vapes" className="text-primary hover:underline">
                    THCA vapes
                  </Link>,{" "}
                  <Link to="/kollektioner/thca-hash" className="text-primary hover:underline">
                    hash
                  </Link> och{" "}
                  <Link to="/kollektioner/thca-buds" className="text-primary hover:underline">
                    buds
                  </Link>.
                </span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-primary font-bold">•</span>
                <span>
                  <strong className="text-foreground">Guider och artiklar</strong> – 
                  Djupgående texter om specifika ämnen som{" "}
                  <Link to="/blogg/terpener" className="text-primary hover:underline">
                    terpener
                  </Link>,{" "}
                  <Link to="/blogg/hur-upphettning-paverkar-thca" className="text-primary hover:underline">
                    dekarboxylering
                  </Link> och{" "}
                  <Link to="/blogg/thcp-vs-thca" className="text-primary hover:underline">
                    skillnader mellan cannabinoider
                  </Link>.
                </span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-primary font-bold">•</span>
                <span>
                  <strong className="text-foreground">FAQ</strong> – 
                  Svar på vanliga frågor om praktiska aspekter som leverans och 
                  produktval. Se{" "}
                  <Link to="/faq" className="text-primary hover:underline">
                    vanliga frågor
                  </Link>.
                </span>
              </li>
            </ul>
            <p className="text-muted-foreground leading-relaxed mt-4">
              Guider fokuserar på informationella frågor som "vad är THCA" eller "hur 
              fungerar dekarboxylering". Produktsidor fokuserar på praktisk information 
              om specifika produkter. Denna uppdelning minskar överlappning och gör 
              det enklare att hitta relevant information.
            </p>
          </div>

          {/* H2: För vem sidan är (och inte är) */}
          <div className="mb-8">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-lg bg-cta/10 flex items-center justify-center">
                <Users className="w-5 h-5 text-cta" />
              </div>
              <h2 className="text-2xl md:text-3xl font-bold text-foreground m-0">
                För vem sidan är – och inte är
              </h2>
            </div>
            <p className="text-muted-foreground leading-relaxed">
              Vapebolag.se riktar sig till vuxna (18+) i Sverige som vill lära sig mer 
              om THCA innan de fattar informerade beslut. Innehållet är utformat för 
              personer som söker saklig information snarare än reklam.
            </p>
            <p className="text-muted-foreground leading-relaxed">
              <strong className="text-foreground">Sidan är inte avsedd för:</strong>
            </p>
            <ul className="text-muted-foreground space-y-2 list-none pl-0 mb-4">
              <li className="flex items-start gap-3">
                <span className="text-accent font-bold">✕</span>
                <span>Personer under 18 år</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-accent font-bold">✕</span>
                <span>Medicinsk rådgivning – vi ger ingen medicinsk vägledning</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-accent font-bold">✕</span>
                <span>Juridisk rådgivning – konsultera en jurist för specifika frågor</span>
              </li>
            </ul>
            <p className="text-muted-foreground leading-relaxed">
              För medicinska frågor, kontakta sjukvården. För juridiska frågor om 
              specifika situationer, kontakta en kvalificerad jurist. Vapebolag.se 
              tillhandahåller endast allmän information för utbildningsändamål.
            </p>
          </div>

          {/* Trust statement */}
          <div className="mt-10 pt-6 border-t border-border/50">
            <p className="text-sm text-muted-foreground">
              Senast uppdaterad: Februari 2026. Informationen på denna sida granskas 
              regelbundet för att säkerställa korrekthet. Har du frågor eller 
              synpunkter? Kontakta oss via{" "}
              <Link to="/kontakt" className="text-primary hover:underline">
                kontaktformuläret
              </Link>.
            </p>
          </div>
        </article>
      </div>
    </section>
  );
});

HomepageSEOHub.displayName = "HomepageSEOHub";
export default HomepageSEOHub;
